import { Clock, Loader2 } from 'lucide-react';

import { useGetEmailPreview } from '~entities/settings/email-settings/api/get-email-preview';
import { useGetStudioLogoImage } from '~entities/studios/api/get-studio-logo-image';
import { useCurrentStudio } from '~entities/studios/model/studio-store';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';

interface EmailPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scenarioId: string;
  scenarioName: string;
}

export const EmailPreviewDialog = ({
  open,
  onOpenChange,
  scenarioId,
  scenarioName,
}: EmailPreviewDialogProps) => {
  const studio = useCurrentStudio();
  const studioId = studio?.locationId ?? '';

  const {
    data: studioLogo,
    isLoading: isLogoLoading,
  } = useGetStudioLogoImage({ studioId });

  const {
    data: preview,
    isPending: isPendingPreview,
    isError,
  } = useGetEmailPreview({
    scenarioId,
    logoUrl: studioLogo?.imageUrl,
    enabled: open && !isLogoLoading,
  });

  const isLoading = isLogoLoading || (open && isPendingPreview);
  // Backend is the source of truth: `previewAvailable === false` means the
  // scenario has no live template yet (WIP), regardless of which scenario it is.
  const isComingSoon = !!preview && preview.previewAvailable === false;
  const hasPreview = !isComingSoon && !!preview?.html;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] w-full max-w-3xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{scenarioName}</DialogTitle>
          <DialogDescription>
            Preview of the email sent to parents
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className='flex justify-center py-12'>
            <Loader2 className='size-8 animate-spin text-muted-foreground' />
          </div>
        ) : hasPreview ? (
          <div className='space-y-4'>
            <div className='rounded-lg border border-border bg-muted/30 px-4 py-3'>
              <p className='text-sm font-medium text-muted-foreground'>
                Subject
              </p>
              <p className='text-base font-semibold'>{preview.subject}</p>
            </div>

            <div className='rounded-xl border border-border bg-white p-2 shadow-sm sm:p-4'>
              <iframe
                title={`${scenarioName} email preview`}
                srcDoc={preview.html}
                sandbox=''
                className='mx-auto h-[640px] w-full max-w-screen-sm rounded-lg border-0 bg-white'
              />
            </div>
          </div>
        ) : isComingSoon ? (
          <div className='flex flex-col items-center gap-3 py-12 text-center'>
            <div className='flex size-12 items-center justify-center rounded-full bg-muted'>
              <Clock className='size-6 text-muted-foreground' />
            </div>
            <p className='text-base font-semibold text-foreground'>
              Preview coming soon
            </p>
            <p className='mx-auto max-w-md text-sm text-muted-foreground'>
              This email is still a work in progress, so a preview isn&apos;t
              available yet. Actual emails are unaffected.
            </p>
          </div>
        ) : (
          <div className='py-12 text-center text-muted-foreground'>
            <p className='text-base font-semibold text-foreground'>
              Unable to load email preview
            </p>
            <p className='mx-auto mt-2 max-w-md text-sm'>
              {isError
                ? 'Something went wrong while loading the preview. Please try again.'
                : 'The template may not be available. Please try again later.'}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
